import type { MultiValue } from 'chakra-react-select';
import { useEffect, useMemo, useState } from 'react';
import Combobox, {
  type ComboboxSize,
  type DefaultOptionType,
} from './combobox';

type ComboboxForPastingProps = {
  validItems: DefaultOptionType[];
  noResultKeys: string[];
  selectedItems: DefaultOptionType[];
  hasError: boolean;
  finalValue: number[] | null;
  isFetching: boolean;
  setFinalValue: (value: number[] | null) => void;
  onSelect: (items: MultiValue<DefaultOptionType>) => void;
  setHasError: (hasError: boolean) => void;
  isDisabled?: boolean;
  size?: ComboboxSize;
};

const ComboboxForPasting = ({
  validItems,
  noResultKeys,
  selectedItems,
  hasError,
  finalValue,
  isFetching,
  setFinalValue,
  onSelect,
  setHasError,
  isDisabled,
  size = 'medium',
}: ComboboxForPastingProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [triggerSelectionUpdate, setTriggerSelectionUpdate] = useState(false);
  const handleKeyDown = (e: React.KeyboardEvent<Element>) => {
    if (e.key === 'Enter') {
      const inputAsList = searchValue
        .trim()
        .split(/[,;\s]+/)
        .filter((item) => item.length > 0)
        .map((item) => Number(item));
      if (inputAsList.length > 0) {
        const selectedIds = validItems.map((item) => Number(item.id));
        const errorIds = noResultKeys.map((key) => Number(key));
        setFinalValue([...selectedIds, ...errorIds, ...inputAsList]);
        setTriggerSelectionUpdate(true);
      }
      setSearchValue('');
    }
  };

  const errorItems: DefaultOptionType[] = useMemo(
    () =>
      noResultKeys.map((key) => ({
        id: key,
        label: key,
        hasError: true,
      })),
    [noResultKeys],
  );

  const allSelectedItems = useMemo(() => {
    const combined = [...selectedItems, ...validItems, ...errorItems];
    const uniqueMap = new Map(combined.map((item) => [item.id, item]));
    return Array.from(uniqueMap.values());
  }, [selectedItems, validItems, errorItems]);

  useEffect(() => {
    if (!isFetching && triggerSelectionUpdate && finalValue !== null) {
      onSelect(allSelectedItems);
      setTriggerSelectionUpdate(false);
    }
  }, [
    isFetching,
    triggerSelectionUpdate,
    allSelectedItems,
    finalValue,
    onSelect,
  ]);

  useEffect(() => {
    setHasError(noResultKeys.length > 0);
  }, [noResultKeys, setHasError]);
  return (
    <Combobox
      options={[]}
      selectedItems={allSelectedItems}
      hasError={hasError}
      isLoading={isFetching}
      onSelect={(selectedItems) => {
        onSelect(selectedItems);
        const selectedIds = selectedItems.map((item) => Number(item.id));
        setFinalValue(selectedIds.length > 0 ? selectedIds : null);
        setHasError(noResultKeys.length > 0);
      }}
      inputValue={searchValue}
      onInputChange={(value: string) => setSearchValue(value)}
      placeholder="Type or paste numbers and press Enter"
      onKeyDown={handleKeyDown}
      isInPasteMode
      isDisabled={isDisabled}
      size={size}
    />
  );
};
export default ComboboxForPasting;
